/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, NgModuleRef, Type, createNgModule, inject } from '@angular/core';

import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { WidgetType } from '@frontend/sports/types/components/widget';
import { EventsService, LazyClientConfigBase } from '@frontend/vanilla/core';
import { uniq } from 'lodash-es';
import { Observable, combineLatest, from, map, mergeMap, of, share, switchMap, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ModuleLoaderService {
    private sportsProductService = inject(SportsProductService);
    private dispatcher = inject(DispatcherService);
    private eventsService = inject(EventsService);

    readonly lazyFeatures: string[] = [
        WidgetType.LinkList,
        WidgetType.Marquee,
        WidgetType.NextToGo,
        WidgetType.TopEvents,
        WidgetType.TabbedGrid,
        WidgetType.OutrightsGrid,
        WidgetType.Composable,
        WidgetType.ShowCase,
        WidgetType.CompetitionPriceBoosts,
        WidgetType.Media,
        WidgetType.MiniGames,
        WidgetType.SportTree,
        WidgetType.BetStationMarquee,
        WidgetType.BetStationTopScreenSportsBanner,
        WidgetType.BetStationLiveTicker,
        WidgetType.BetStationSports,
        WidgetType.BetStationSportsBanner,
        WidgetType.BetstationEventList,
        WidgetType.BetStationRacing,
        WidgetType.PopularMultiBets,
        WidgetType.PanicButton,
        WidgetType.TimeFilters,
        WidgetType.HiddenMarket,
        WidgetType.Favourites,
        WidgetType.BetColumn,
        WidgetType.BetGenerator,
        WidgetType.CompetitionStandings,
        WidgetType.PromoContainer,
        WidgetType.StoryContent,
        WidgetType.TopItems,
        WidgetType.ScoreboardCarousel,
    ];

    private loadedModules: Map<string, Observable<NgModuleRef<any>> | NgModuleRef<any>>;
    private loadedFeatures = new Map<string, boolean>();

    constructor() {
        this.loadedModules = new Map<string, Observable<NgModuleRef<any>> | NgModuleRef<any>>();
    }

    loadModule<T>(moduleName: string, moduleLoader: () => Promise<Type<T>>, ...lazyConfigs: LazyClientConfigBase[]): Observable<NgModuleRef<T>> {
        const loadedModule = this.loadedModules.get(moduleName);
        if (loadedModule) {
            if (loadedModule instanceof Observable) {
                return loadedModule;
            }

            return of(loadedModule);
        }
        const lazyConfigsObservable = lazyConfigs?.length ? combineLatest(lazyConfigs.map((c) => c.whenReady)) : of({});

        const observable = from(moduleLoader()).pipe(
            switchMap((module) => (lazyConfigsObservable ? lazyConfigsObservable.pipe(map((_) => module)) : of(module))),
            mergeMap((moduleType) => of(createNgModule(moduleType, this.sportsProductService.getDynamicInjector()))),
            tap((ref) => {
                this.loadedModules.set(moduleName, ref);
                this.dispatcher.dispatch('MODULE_LOADED', moduleName);
            }),
            share(),
        );
        this.loadedModules.set(moduleName, observable);

        return observable;
    }

    lazyLoadFeatures(eventName: string | string[]) {
        const events = uniq(Array.isArray(eventName) ? eventName : [eventName]);
        events
            .filter((event) => this.lazyFeatures.includes(event) && !this.loadedFeatures.has(event))
            .forEach((event) => {
                this.loadedFeatures.set(event, true);
                this.eventsService.raise({ eventName: event });
            });
    }
}
